/**
 * Menu Theme Hooks
 * 
 * Custom hooks to access menu theme customization
 */

import { useContext } from 'react';
import { MenuThemeContext } from '@/contexts/MenuThemeContext';
import type { MenuCustomization } from '@/types/menuCustomization';

/**
 * Hook to access menu theme customization
 */
export function useMenuTheme() {
  const context = useContext(MenuThemeContext);
  
  if (!context) {
    throw new Error('useMenuTheme must be used within a MenuThemeProvider');
  }
  
  return context;
}

/**
 * Hook to access only the customization object
 */
export function useMenuCustomization(): MenuCustomization {
  const { customization } = useMenuTheme();
  return customization;
}

/**
 * Hook to access only specific colors
 */
export function useMenuColors() {
  const { customization } = useMenuTheme();
  return customization.colors;
}

/**
 * Hook to access only typography settings
 */
export function useMenuTypography() {
  const { customization } = useMenuTheme();
  return customization.typography;
}

/**
 * Hook to access layout settings
 */
export function useMenuLayout() {
  const { customization } = useMenuTheme();
  return customization.layout;
}
